import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { ThemeProvider, Theme } from '@mui/material/styles';
import { buildTheme } from '../theme/buildTheme';
import { DEFAULT_DARK_TONE, DarkToneName, isDarkToneName } from '../theme/refinedDark';
import { ThemeContextType } from '../types';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a CustomThemeProvider');
  }
  return context;
};

interface CustomThemeProviderProps {
  children: ReactNode;
}

export const CustomThemeProvider: React.FC<CustomThemeProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<'light' | 'dark'>('light');
  const [darkTone, setDarkToneState] = useState<DarkToneName>(() => {
    const stored = localStorage.getItem('darkTone');
    return isDarkToneName(stored) ? stored : DEFAULT_DARK_TONE;
  });

  // Initialize theme from localStorage
  useEffect(() => {
    const storedMode = localStorage.getItem('themeMode') as 'light' | 'dark';
    if (storedMode) {
      setModeState(storedMode);
    } else {
      // Check system preference
      const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setModeState(systemPreference);
    }
  }, []);

  const setMode = (newMode: 'light' | 'dark') => {
    setModeState(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const setDarkTone = (tone: DarkToneName) => {
    setDarkToneState(tone);
    localStorage.setItem('darkTone', tone);
  };

  const theme: Theme = useMemo(() => buildTheme(mode, darkTone), [mode, darkTone]);

  const value: ThemeContextType = {
    mode,
    setMode,
    darkTone,
    setDarkTone,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}; 